import { BaseEditor, BaseRange } from 'slate/dist/index.js';
import { ReactEditor } from 'slate-react/dist/index.js';
import { SuggestedFollowee } from 'js/core_post_webpack/models/user';
import { Video } from 'js/videos/shared/video_helper';

// To allow merging of reason types, we need to define some helper types which
// accomplish this.
type GetKeys<U> = U extends Record<infer K, any> ? K : never;

type UnionToIntersection<U extends object> = {
  [K in GetKeys<U>]: U extends Record<K, infer T> ? T : never;
};

// Custom Slate elements for social
export type CustomText = {
  text: string;
  link?: string;
  hashtag?: boolean;
  readOnly?: boolean;
};
export type CustomElement = {
  type: CustomElementType;
  data?: any;
  children: (CustomText | CustomElement)[];
};
export type CustomElementType = 'tag' | 'paragraph' | 'paper_link';

// Let Slate know about our custom types
declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

interface User {
  id: number;
  firstName: string;
  middleInitials: string | null;
  lastName: string;
  pageName: string;
  domainName: string;
  createdAt: string;
  displayName: string;
  url: string;
}

export interface FollowableUser extends User {
  photo: string;
  following: boolean;
  university: {
    id: number;
    displayName: string;
    url: string;
  } | null;
  department?: {
    id: number;
    displayName: string;
  } | null;
  position?: string | null;
  followers: number;
  openalexAuthor?: {
    citedByCount: number;
    hIndex?: number;
  } | null;
  worksCount?: number;
}

export type SuggestionMetadata = {
  UniversityColleagues: {
    department: string;
  };
  FollowedByFollowees: {
    followees: {
      display_name: string;
      photo: string | null;
    }[];
    count: number;
  };
  AuthorsBookmarked: {
    works: {
      title: string;
      publication: string | null;
      publication_year: string | number | null;
      thumbnail: string | null;
    }[];
    count: number;
  };
  TopResearchInterestUsers: {
    mutual_ris: string[];
  };
  FeaturedResearchInterestUser: {
    followings: number;
    papers: number;
  };
};

export type AiButtonType = 'figure' | 'finding';

export type Suggestion = {
  connecting_users?: Array<object>;
  sources?: Array<string>;
  metadata: SuggestionMetadata;
  user: SuggestedFollowee;
  scoring_metadata?: { [key: string]: any };
};

export type SuggestionGroup = {
  id: string;
  name: string;
  suggestions: Suggestion[];
};

export type CoauthorSuggestion = {
  name: string;
  user_id?: string;
  obfuscated_email?: string;
  encrypted_email?: string;
  tags: CoauthorTag[];
  profile_picture_url: string;
  profile_url?: string;
  affiliated_university?: string;
};

export type CoauthorTag = {
  id?: number | null;
  name: string;
  title: string;
  work_id: number;
  tagging_user_id: number;
  tagged_user_id?: number | null;
  co_author_invite_id?: number | null;
  email?: string | null;
  encrypted_email?: string | null;
  affiliation?: string | null;
  source_details: any;
  display_order?: number | null;
};

export type Author = {
  userId: number;
  displayName: string;
  profileUrl?: string;
};

export interface UserLike {
  affiliation: string;
  displayName: string;
  id: number;
  photo: string;
  profileUrl: string;
}

export interface DetailedReactions {
  like: UserLike[];
}

export interface Reaction {
  count: number;
  reacted: boolean;
}

export interface Reactions {
  like: Reaction;
  repost: Reaction;
}

export interface Comment {
  id: number | string;
  content: string;
  tags: Tag[];
  hashtags: Hashtag[];
  userId: number;
  createdAt: string;
  expandByDefault: boolean;
  reactions: Reactions;
  replies: Comments;
}

export interface Comments {
  count: number;
  data: Comment[];
  hasMore: boolean;
}

export interface RawScoreData {
  has_unseen_source_user_comment: boolean;
  times_seen: number;
  last_view_age: number | null | undefined;
  first_view_age: number | null | undefined;
  created_by_current_user: boolean;
  commented_on_by_current_user: boolean;
  recommended_relevance_score: number | null | undefined;
  is_non_textual: boolean;
  repost_user_count: number;
  comment_user_count: number;
  like_user_count: number;
  not_interested_similarity: number;
  raw_age: number;
  latest_activity_age: number;
  final_age: number;
  content_type: string;
}

export interface PostReasonData {
  source_user_post: undefined;
  source_user_like: {
    sourceUserIds: number[];
    count: number;
  };
  source_user_repost: {
    sourceUserIds: number[];
    count: number;
  };
  source_user_comment: {
    sourceUserIds: number[];
    count: number;
  };
  source_user_reply: {
    sourceUserIds: number[];
    count: number;
  };
  own_post: undefined;
  boosted: undefined;
  recommended: undefined;
  tagged_in_post: undefined;
  tagged_in_comment: undefined;
  fallback_content: undefined;
  current_user_comment: undefined;
}

export interface PostReason {
  type: keyof PostReasonData;
  data?: Partial<
    UnionToIntersection<NonNullable<PostReasonData[keyof PostReasonData]>>
  >;
}

export type NewsType =
  | 'post'
  | 'paper_update'
  | 'work'
  | 'openalex_work'
  | 'paper_set';

interface AbstractNews<T extends NewsType> {
  newsId: string | number;
  newsType: T;
  paperType: PaperType | null;
  content: string;
  tags: Tag[];
  hashtags: Hashtag[];
  work?: Paper | OpenalexWork;
  newsStory?: NewsStory;
  hoisted?: boolean;
  looksDeleted?: boolean;
  isQuotedNews?: boolean;
  recommendationScore?: number;
  rawScoreData: RawScoreData;
  impression_tracking_id?: string;
}

export type NewsPreview = {
  content?: string;
  tags: Tag[];
  hashtags: Hashtag[];
  image?: Image;
  work?: Paper;
  quotable?: SlimPost;
  users: FollowableUser[];
  reason: string;
  numLikes: number;
  numComments: number;
  user: FollowableUser;
  createdAt: string;
  newsType: NewsType;
};

export interface SlimPost extends AbstractNews<'post'> {
  image?: Image;
  userId: number;
  createdAt: string;
  looksDeleted: boolean;
}

export interface Post extends AbstractNews<'post'> {
  reason: PostReason;
  image?: Image;
  video?: Video;
  userId: number;
  createdAt: string;
  reactions: Reactions;
  comments: Comments;
  views?: number;
  quotable?: SlimPost;
}

export interface PaperUpdateReasonData {
  source_user_recommended: undefined;
  source_user_uploaded: undefined;
  source_user_bookmarked: undefined;
  source_user_like: {
    sourceUserIds: number[];
    count: number;
  };
  source_user_comment: {
    sourceUserIds: number[];
    count: number;
  };
  source_user_reply: {
    sourceUserIds: number[];
    count: number;
  };
  source_user_repost: {
    sourceUserIds: number[];
    count: number;
  };
  own_paper_update: undefined;
  boosted: undefined;
  recommended: undefined;
  fallback_content: undefined;
  current_user_comment: undefined;
  auto_group_new_paper: {
    autoGroupName: string;
    sources: string[];
  };
  auto_group_liked: {
    autoGroupName: string;
    sourceUserId: number;
    userFollowsSourceUser: boolean;
    sources: string[];
  };
  auto_group_comment: {
    autoGroupName: string;
    sourceUserId: number;
    userFollowsSourceUser: boolean;
    sources: string[];
  };
  autogroup_enhanced: undefined;
  autogroup_plain: undefined;
}

export interface PaperUpdateReason {
  type: keyof PaperUpdateReasonData;
  data?: Partial<
    UnionToIntersection<
      NonNullable<PaperUpdateReasonData[keyof PaperUpdateReasonData]>
    >
  >;
}

export type PaperType =
  | 'certified_work'
  | 'paper_upload'
  | 'bookmark'
  | 'attached';

export interface PaperUpdate extends AbstractNews<'paper_update'> {
  reason: PaperUpdateReason;
  userId: number;
  createdAt: string;
  reactions: Reactions;
  comments: Comments;
  views?: number;
  work: Paper;
  hoisted: boolean;
}

export interface OpenAlexRecommendation extends AbstractNews<'openalex_work'> {
  reason: PaperUpdateReason;
  work: OpenalexWork;
  hoisted: boolean;
  // Comments and Reactions will be empty
  reactions: Reactions;
  comments: Comments;
  // The below values are always expected to be undefined
  views?: number;
  createdAt?: string;
  userId?: number;
  recommendationMetrics?: VoteCountingRecommendationMetrics;
}

export interface Work extends AbstractNews<'work'> {
  reason: PaperUpdateReason;
  work: Paper;
  hoisted: boolean;
  reactions: Reactions;
  comments: Comments;
  // The below values are always expected to be undefined
  views?: number;
  createdAt?: string;
  userId?: number;
  recommendationMetrics?: VoteCountingRecommendationMetrics;
  autogroupData?: {
    autogroupId?: number;
    autogroupTitle?: string;
    autogroupSource?: string;
    action: 'save' | 'view';
    relevantUserIds: number[];
  };
}

export interface PaperSet extends AbstractNews<'paper_set'> {
  reason: PaperUpdateReason;
  hoisted: boolean;
  reactions: Reactions;
  comments: Comments;
  // The below values are always expected to be undefined
  views?: number;
  createdAt?: string;
  userId?: number;
  recommendationMetrics?: VoteCountingRecommendationMetrics;
}

export interface VoteCountingRecommendationMetrics {
  // recommendation metrics usually have a rawFeedScore, maybe other properties
  rawFeedScore?: number;
  seed_type?: string;
  seed_content_type?: string;
  seed_content_id?: string | number;
  [key: string]: boolean | string | number | undefined;
}

export const isPaper = (work?: Paper | OpenalexWork): work is Paper => {
  if (!work) return false;
  return typeof work.id === 'number';
};

export type News =
  | Post
  | PaperUpdate
  | OpenAlexRecommendation
  | Work
  | PaperSet;

export enum ResourceType {
  Post = 'post',
  PaperUpdate = 'paper_update',
  Comment = 'comment',
  OpenalexWork = 'openalex_work',
  Work = 'work',
  PaperSet = 'paper_set',
}

export enum DismissalReason {
  Ignored = 'ignored',
  Reported = 'reported',
}

export enum ReactionType {
  Like = 'like',
  Repost = 'repost',
}

export type Tag = {
  id?: number | string;
  tagId: number;
  tagType: 'user';
  location: number;
  text?: string;
  url?: string;
};

export type Hashtag = {
  id: number | string;
  hashtag: string;
};

export type AutocompleteType = 'hashtag' | 'tag';

export type AutocompleteResult = {
  type?: AutocompleteType;
  range?: BaseRange;
  search?: string;
};

export type Image = {
  id: number | string;
  url: string;
  width: number | null;
  height: number | null;
  size: number | null;
};

export type PaperImage = {
  id: number;
  type: string;
  figureNum: number;
  pageNum: number;
  caption: string;
  url: string;
  thumbnailUrl: string;
  width: number;
  height: number;
};

export type NewsStory = {
  id: number;
  heading: string;
  body: string;
  footer: string;
  type: string;
  multiPaper: boolean;
  papers?: Array<Paper | OpenalexWork>;
  showAiButton: boolean;
  newsType: NewsType;
  newsId: string | number;
};

export type Paper = {
  id: number;
  title: string;
  slug: string;
  url: string;
  summary?: string;
  researchInterests?: string[];
  publicationYear?: string | number;
  publication?: string;
  authors: Author[];
  attachment?: {
    id: number;
    readable: boolean;
    thumbnailUrl: string;
    pdfUrl: string;
    pageCount: number;
  };
  images?: PaperImage[];
  citation?: string;
  bookmarkedByCurrentUser: boolean;
  bookmarkCount: number;
  allTimeViews?: number;
  formattedCitations?: { name: string; citation: string }[];
  podcast?: {
    id: number;
    workId: number;
    workTitle: string;
    generationStartedAt: string | null;
    generationCompletedAt: string | null;
    streamReadyAt: string | null;
    transcriptCompletedAt: string | null;
    visibility: string;
    duration: number | null;
  };
  comic?: {
    id: number;
    workId: number;
    workTitle: string;
    contentUrl: string;
    visibility: string;
  };
};

export type OpenalexWork = Omit<Paper, 'id'> & {
  id: string;
};

export type PaperLink = {
  id: number;
  slug: string;
};

export type ResourcePayload = {
  content: string;
  tags: Tag[];
  quotableType?: string;
  quotableId?: string | number;
  image?: Image;
  paper?: Paper;
};

export interface PaperUpdatePayload {
  content: string;
  tags: Tag[];
  workId: number;
  entityId: number;
  entityType: string;
  newsType: 'certified_work' | 'paper_upload' | 'bookmark';
}

export interface PaperUpdateUnlinked {
  title: string;
  slug?: string;
  url?: string;
  summary?: string;
  researchInterests?: string[];
  publicationYear?: string | number;
  publication?: string;
  authors: Author[];
  attachment?: {
    id: number | null;
    readable: boolean | null;
    thumbnailUrl: string | null;
    pdfUrl: string | null;
    pageCount: number | null;
  };
  podcast?: {
    id: number;
    workId: number;
    workTitle: string;
    generationStartedAt: string | null;
    generationCompletedAt: string | null;
    streamReadyAt: string | null;
    transcriptCompletedAt: string | null;
    visibility: string;
    duration: number | null;
  };
  comic?: {
    id: number;
    workId: number;
    workTitle: string;
    contentUrl: string;
    visibility: string;
  };
  isRecommended?: boolean;
}

// Note: authors is an array of strings here rather than Authors because it interfaces with
// an existing endpoint also used elsewhere, which returns an array of strings for authors
export interface WorkWithMetadata {
  id: number;
  authors: string[];
  index: number;
  fileType?: string;
  scribdThumbnail: string | null;
  title: string;
  url: string;
  viewCount: number;
  publication: string | null;
  createdAt: string;
}

type Not<T> = [T] extends [never] ? unknown : never;
type Extractable<T, U> = Not<
  U extends unknown ? Not<T extends U ? unknown : never> : never
>;
/**
 * HACK: asEnum is added here so that we can cast strings to a enum
 * safely. The proper way to do this with typescript 4.1 is to use
 * template literal types, which will allow us to extract the strings
 * from a enum into a type, e.g.
 *
 *  `` `${ResourceType}` `` -> `"post" | "paper_update" | "comment"`
 *
 * However, we are not able to do this because the version of Astroturf
 * at the time of this commit is still using babel version < 7.12.0,
 * which does not support template literal types :(
 *
 * Source: https://stackoverflow.com/a/52373525
 */
export const asEnum = <
  E extends Record<keyof E, string | number>,
  K extends string | number,
>(
  e: E,
  k: K & Extractable<E[keyof E], K>,
): Extract<E[keyof E], K> => {
  // runtime guard, shouldn't need it at compiler time
  if (Object.values(e).indexOf(k) < 0)
    throw new Error('Expected one of ' + Object.values(e).join(', '));
  return k as Extract<E[keyof E], K>;
};
